import { getEnv } from "utils/env";

// Mission → OAuth2 scope mapping (must match client registrations in Hydra).
export const MISSION_OAUTH2_SCOPE = {
  myaccount: "openid offline",
  gol: "openid offline gol-session",
  nectar: "openid offline",
  evc: "openid offline",
  savingcard: "openid offline gol-session",
};

// Session-storage key used to break infinite redirect loops.
export const OAUTH2_REDIRECT_GUARD = "oauth2_flow_initiated";

// How long (ms) to honour the redirect guard before treating it as stale.
// Chrome DevTools "toggle mobile device" can trigger a page reload that loses
// the ?login_challenge= query param.  Without an expiry the guard would block
// re-initiation indefinitely.
export const OAUTH2_REDIRECT_GUARD_TTL_MS = 60_000; // 60 seconds

/**
 * Returns true if the redirect guard is currently active (i.e. we have already
 * redirected to Hydra and should wait for the challenge to come back).
 * Treats a guard older than OAUTH2_REDIRECT_GUARD_TTL_MS as stale and removes it.
 */
export const isOAuth2RedirectGuardActive = () => {
  const raw = sessionStorage.getItem(OAUTH2_REDIRECT_GUARD);
  if (!raw) return false;
  const ts = Number(raw);
  if (!ts || Date.now() - ts > OAUTH2_REDIRECT_GUARD_TTL_MS) {
    sessionStorage.removeItem(OAUTH2_REDIRECT_GUARD);
    return false;
  }
  return true;
};

export const clearOAuth2RedirectGuard = () => {
  sessionStorage.removeItem(OAUTH2_REDIRECT_GUARD);
};

/**
 * Build and navigate to the Hydra /oauth2/auth endpoint to start an
 * authorization code flow when the SPA loads without a login_challenge.
 *
 * CRITICAL — REACT_APP_HYDRA_OAUTH_HOST must equal Hydra's URLS_SELF_ISSUER.
 *
 * Why: Hydra stores a oauth2_authentication_csrf_insecure cookie when the
 * browser first calls /oauth2/auth.  The cookie has no explicit Domain
 * attribute so the browser stores it for the EXACT origin of the response.
 * The login_verifier and consent_verifier redirects are constructed from
 * URLS_SELF_ISSUER.  If the initial /oauth2/auth call goes through a
 * different origin (e.g. the nginx proxy at login-ui), the cookie is set
 * on the nginx origin.  When Hydra processes the login_verifier at its
 * own URLS_SELF_ISSUER origin, the browser does not send the cookie →
 * "No CSRF value available in the session cookie".
 *
 * Both dev and stg: REACT_APP_HYDRA_OAUTH_HOST must be the direct Hydra
 * public endpoint URL (id-ory-hydra.ext.<env>.jspaas.uk) which equals
 * URLS_SELF_ISSUER.  The nginx /oauth2/ proxy is only used for the
 * /oauth2/token exchange in CallbackPage (a server-side POST, not a
 * browser navigation, so CSRF cookies are irrelevant there).
 *
 * The redirect_uri always uses window.location.origin/callback so the auth
 * code is returned to whichever login-ui instance the user started from.
 */
export const initiateOAuth2Flow = (mission) => {
  const scope = MISSION_OAUTH2_SCOPE[mission];
  if (!scope) return; // Unknown mission – bail out.

  if (isOAuth2RedirectGuardActive()) return;

  // Use /{mission}/callback (mission segment in the path, not the query string).
  const redirectUri = `${window.location.origin}/${mission}/callback`;
  const state =
    Math.random().toString(36).substring(2) + Date.now().toString(36);

  const params = new URLSearchParams({
    client_id: mission,
    response_type: "code",
    missionId: mission,
    scope,
    redirect_uri: redirectUri,
    state,
  });

  // Navigate DIRECTLY to Hydra's external URL so the _csrf cookie is stored on
  // the Hydra domain.  Fall back to the nginx proxy path only in local dev
  // (where REACT_APP_HYDRA_OAUTH_HOST is not set and there is no real Hydra).
  const hydraHost = getEnv("REACT_APP_HYDRA_OAUTH_HOST");

  if (!hydraHost && process.env.NODE_ENV !== "development") {
    console.error(
      "[id-account-ui-login] REACT_APP_HYDRA_OAUTH_HOST is not configured. " +
        "This must equal the Hydra admin URLS_SELF_ISSUER so the _csrf " +
        "cookie domain matches the login_verifier redirect domain. " +
        "Check env-configs/<env>/override.yaml and docker-entrypoint.sh."
    );
  }

  const authUrl = hydraHost ? `${hydraHost}/oauth2/auth` : `/oauth2/auth`; // local dev only

  sessionStorage.setItem(OAUTH2_REDIRECT_GUARD, String(Date.now()));
  window.location.href = `${authUrl}?${params.toString()}`;
};
